<template>
  <Dialog
    ref="drawer"
    v-bind="$attrs"
    :visible="active"
    :position="side"
    :append-to="appendTo"
    :dt="designTokens"
    :show-close-icon="true"
    :draggable="isDraggable"
    :class="drawerClasses"
    :close-button-props="{
      'data-sb-automation': closeVariation === 'back' ? 'close-drawer-button' : 'close-x-drawer-button',
      class: [closeVariation === 'back' ? $style.quitBackContainer : $style.quitXContainer,]
    }"
    :pt="{
      ...get($attrs, 'pt', {}),
      root: {
        'data-sb-automation': 'drawer-root',
        'data-sb-regression': 'sb-drawer',
        ...get($attrs, 'pt.root', {}),
      },
      header: {
        ...get($attrs, 'pt.header', {}),
        class: [$style.header,
                hasHeaderSlot($slots['drawer-header']) && $style.headerContent,
                closeVariation === 'x' ? $style.headerRow : $style.headerNoTopPadding,
                'sb-drawer__header',
                get($attrs, 'pt.header.class'),
        ],
      },
      footer: {
        ...get($attrs, 'pt.footer', {}),
        class: [hasHeaderSlot($slots['drawer-footer']) && $style.footer, get($attrs, 'pt.footer.class')]
      },
      content: {
        ...get($attrs, 'pt.content', {}),
        class: [
          $style.content,
          'sb-drawer__well',
          get($attrs, 'pt.content.class'),
        ]
      },
      mask: {
        ...get($attrs, 'pt.mask', {}),
        id: maskId,
        class: [closeOnOutsideClick ? $style.mask : '', get($attrs, 'pt.mask.class')],
      }
    }"
    @update:visible="$emit('update:active', $event)"
    @show="closeOnOutsideClick && registerMaskHandler()"
    @hide="closeOnOutsideClick && unregisterMaskHandler()"
  >
    <template #closeicon>
      <back-icon
        v-if="closeVariation == 'back'"
        :class="[$style.quitBackIcon, $style['quitBackIcon--' + side]]"
        @click.prevent="handleClose"
      />
    </template>
    <template #header>
      <slot
        name="drawer-header"
        :close="handleClose"
      />
    </template>
    <template
      v-if="$slots['drawer-container']"
      #container
    >
      <slot
        name="drawer-container"
        :close="handleClose"
      />
    </template>
    <slot />
    <template #footer>
      <slot name="drawer-footer" />
    </template>
  </Dialog>
  <Drawer :class="$style.invisible" />
</template>

<script setup>
/* eslint-disable css-modules/no-unused-class */
import Dialog from 'primevue/dialog';
import Drawer from 'primevue/drawer';
import { defineProps, defineEmits, computed, onMounted, onUnmounted, useCssModule, useSlots, useAttrs, defineOptions, ref, provide, inject } from 'vue';
import { hideChat, unHideChat } from '@src/utils/chatHelper';
import { useShadowDom } from '@src/mixins/useShadowDom';
import get from 'lodash/get';
import BackIcon from '@src/components/Icons/components/BackIcon.vue';
import drawerProps from '@src/components/Drawer/props';

const drawer = ref(null);
provide('parentRef', drawer);
const attrs = useAttrs();
const parentRef = inject('parentRef', null);

const maskId = `drawer-mask-container${parentRef?.value.id.replace(/^/, '-') || ''}`;

defineOptions({
  name: 'SbDrawerNext',
});

const emit = defineEmits(['update:active']);
const slots = useSlots();

const props = defineProps(drawerProps);
const $style = useCssModule('classes');

function bypassHideAndShow() {
  return props.side === 'left';
}
function hideHelpChat() {
  if (bypassHideAndShow()) return;
  /* Hide help chat button on drawer open */
  hideChat();
}
function showHelpChat(document) {
  if (bypassHideAndShow()) return;
  /* Show help chat button on drawer open */
  const openDrawers = document?.getElementsByClassName('p-drawer');
  if (openDrawers?.length) return; // leave hidden if more drawers remain open
  unHideChat();
}
function hasHeaderSlot(headerSlot) {
  return headerSlot?.();
}

const isDraggable = computed(() => {
  return attrs.draggable === true;
});

const drawerClasses = computed(() => {
  return [$style.sbDrawer,
    $style[`sbDrawer--${props.side}`],
    hasHeaderSlot(slots['drawer-footer']) && $style.sbDrawerWithFooter,
    attrs.class,
    props.containerClass,
  ].filter(Boolean);
});

const appendTo = props.appendTo || (parentRef?.value ? 'self' : useShadowDom());

onMounted(() => {
  if (props.initOverrides) {
    props.initOverrides(document);
  } else hideHelpChat();
});
onUnmounted(() => {
  if (props.destroyOverrides) {
    props.destroyOverrides();
  } else {
    showHelpChat();
  }
});

/** Register mask click listener to close the modal on clicking the mask elemeent */
/* Dialog.dismissableMask prop is not working */
const maskClickHandler = ref((event) => {
  if (event.target.id !== maskId || !props.closeOnOutsideClick) return;
  emit('update:active', false);
});

function getMaskContainer() {
  if (appendTo === 'self' || !appendTo) {
    return document.getElementById(maskId);
  }
  return appendTo.getElementById(maskId);
}

function registerMaskHandler() {
  const maskContainer = getMaskContainer();
  if (maskContainer) maskContainer.addEventListener('click', maskClickHandler.value);
}

function unregisterMaskHandler() {
  const maskContainer = getMaskContainer();
  if (maskContainer) maskContainer.removeEventListener('click', maskClickHandler.value);
}

function handleClose() {
  emit('update:active', false);
}
const designTokens = computed(() => {
  const tokens = {};

  if (props.removePadding) {
    tokens['header.padding'] = 0;
    tokens['content.padding'] = 0;
    tokens['footer.padding'] = 0;
  }

  return tokens;
});
</script>
<style lang="scss" module="classes">
@import '@src/components/Drawer/style/sb-drawer.module.scss';
</style>
